import Vue from 'vue'
import Vuex from 'vuex'
import { Card, Deck, RANKS, SUITS } from '@/utils/poker'
import { detectSpecialHand } from '@/utils/specialHands'
import { compareFiveCards, compareThreeCards, evaluateFiveCards, evaluateThreeCards, isValidLayout } from '@/utils/handEvaluator'
import { buildDealingQueue } from '@/utils/dealQueue'
import multiplayer from './modules/multiplayer'

Vue.use(Vuex)

const EXTRA_SUIT_ORDER = ['hearts', 'spades', 'clubs', 'diamonds']

/**
 * 单机模式游戏状态中心。
 *
 * 职责边界：
 * - 保存玩家、手牌、三道 layout、倒计时、比牌结果和历史记录；
 * - 驱动单机发牌、自动摆牌、比牌、结算；
 * - 与 Game.vue 保持同一套状态名：idle/dealing/arranging/comparing/settling。
 *
 * 在线模式的房间、WebSocket 状态放在 modules/multiplayer.js；
 * 后端同构规则位于 GameService.java 和 HandEvaluator.java。
 */

// 生成唯一ID，用于创建玩家等场景
function generateId() {
  return Math.random().toString(36).substr(2, 9)
}

/**
 * 创建 AI 玩家对象。
 * index 用于生成默认名称和循环头像；其余字段与后端 Player 模型保持近似结构。
 */
function createAIPlayer(index) {
  return {
    id: generateId(),
    name: `电脑${index + 1}`,
    avatar: `ai-${(index % 4) + 1}`,
    isAI: true,
    isReady: true,
    hands: [],
    layout: {
      front: [],
      middle: [],
      back: []
    },
    isFolded: false,
    specialHand: null,
    score: 0,
    totalScore: 0,
    roomRateScore: 0,
    totalRoomRateScore: 0,
    isLayoutConfirmed: false
  }
}

/**
 * 创建本地人类玩家对象。
 * 单机模式固定以该玩家作为当前操作人；在线模式会由 multiplayer 模块同步房间玩家。
 */
function createHumanPlayer(name) {
  return {
    id: 'player-' + generateId(),
    name: name || '玩家',
    avatar: 'player',
    isAI: false,
    isReady: true,
    hands: [],
    layout: {
      front: [],
      middle: [],
      back: []
    },
    isFolded: false,
    specialHand: null,
    score: 0,
    totalScore: 0,
    roomRateScore: 0,
    totalRoomRateScore: 0,
    isLayoutConfirmed: false
  }
}

function normalizeExtraSuits(playerCount, selectedSuits = []) {
  const requiredTotal = Math.max(0, playerCount - 4)
  if (requiredTotal === 0) return []

  const expanded = []
  if (Array.isArray(selectedSuits)) {
    selectedSuits.forEach(suit => {
      if (EXTRA_SUIT_ORDER.includes(suit)) {
        expanded.push(suit)
      }
    })
  } else if (selectedSuits && typeof selectedSuits === 'object') {
    EXTRA_SUIT_ORDER.forEach(suit => {
      const count = Math.max(0, Number(selectedSuits[suit]) || 0)
      for (let i = 0; i < count; i++) {
        expanded.push(suit)
      }
    })
  }

  if (expanded.length === requiredTotal) {
    return expanded
  }

  const fallback = []
  for (let i = 0; i < requiredTotal; i++) {
    fallback.push(EXTRA_SUIT_ORDER[i % EXTRA_SUIT_ORDER.length])
  }
  return fallback
}

function pickRowSpecialScore(config = {}, key, fallback) {
  const scores = config.rowSpecialScores || {}
  const value = Number(scores[key])
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback
}

/**
 * 发牌测试手牌（三顺子：头道鬼牌当 4，凑成 345）
 * 摆法：头道鬼+♦3+♥5；中道♣4/5/6+♦7+♥8；尾道♠10+♥J+♦Q+♣K+♥A
 */
function buildTestCards() {
  return [
    // 头道：鬼 ♦3 ♥5（鬼当 4，即 345）
    new Card(SUITS.JOKER, RANKS.JOKER_RANK, 'big'),
    new Card(SUITS.DIAMONDS, RANKS.THREE),
    new Card(SUITS.HEARTS, RANKS.FIVE),
    // 中道：♣4 ♣5 ♣6 ♦7 ♥8
    new Card(SUITS.CLUBS, RANKS.FOUR),
    new Card(SUITS.CLUBS, RANKS.FIVE),
    new Card(SUITS.CLUBS, RANKS.SIX),
    new Card(SUITS.DIAMONDS, RANKS.SEVEN),
    new Card(SUITS.HEARTS, RANKS.EIGHT),
    // 尾道：♠10 ♥J ♦Q ♣K ♥A
    new Card(SUITS.SPADES, RANKS.TEN),
    new Card(SUITS.HEARTS, RANKS.JACK),
    new Card(SUITS.DIAMONDS, RANKS.QUEEN),
    new Card(SUITS.CLUBS, RANKS.KING),
    new Card(SUITS.HEARTS, RANKS.ACE)
  ]
}

function buildConfiguredDeck(playerCount, selectedSuits = [], ghostMode = 0) {
  const allCards = [...new Deck().cards]
  const ranks = Object.values(RANKS).filter(r => r.name !== 'Joker')

  // 花牌列表中每个花色条目代表新增一整副该花色（可重复，如 ['hearts', 'hearts']）
  normalizeExtraSuits(playerCount, selectedSuits).forEach((suitName, extraIndex) => {
    const suit = Object.values(SUITS).find(item => item.name === suitName)
    if (!suit) return
    const copyIndex = extraIndex + 1
    ranks.forEach(rank => {
      allCards.push(new Card(suit, rank, undefined, copyIndex))
    })
  })

  // 添加鬼牌：单鬼模式加1张大鬼，双鬼模式加2张
  const jokerSuit = SUITS.JOKER
  const jokerRank = RANKS.JOKER_RANK
  if (ghostMode === 1) {
    allCards.push(new Card(jokerSuit, jokerRank, 'big'))
  } else if (ghostMode === 2) {
    allCards.push(new Card(jokerSuit, jokerRank, 'big'))
    allCards.push(new Card(jokerSuit, jokerRank, 'small'))
  }

  return allCards
}

export default new Vuex.Store({
  state: {
    // 玩家身份
    playerId: localStorage.getItem('playerId') || ('player-' + Math.random().toString(36).substr(2, 9)),
    playerName: localStorage.getItem('playerName') || '',

    // 游戏配置
    config: {
      playerCount: 2,      // 玩家总数（人类+AI）
      baseScore: 1,        // 底分倍数
      arrangeTime: 60,     // 摆牌倒计时（秒）
      enableRandomSeat: true,   // 是否随机入座
      enableIpLimit: true,      // 是否限制同IP加入
      enableSpecialHands: true, // 是否启用特殊牌型
      enableHomeRun: true,      // 是否启用全垒打额外翻倍
      allowOddCards: false,     // 特殊牌型是否叠加奇牌分
      allowFouled: false,       // 是否允许倒水牌继续出牌
      enableHorse: false,
      horseSuit: 'hearts',
      horseRank: 1,
      enableYucai: false,
      yucaiScore: 0,
      allowRoomRate: false,
      roomRatePercent: 2,
      dragonScore: 15,
      allBigScore: 10,
      allSmallScore: 10,
      sixPairsHalfScore: 7,
      threeFlushScore: 7,
      threeStraightScore: 7,
      rowSpecialScores: {
        frontThreeKind: 3,
        middleFullHouse: 2,
        middleFourKind: 8,
        middleStraightFlush: 10,
        middleFiveKind: 20,
        backFourKind: 4,
        backStraightFlush: 5,
        backFiveKind: 10
      },
      sweepScoreType: 'score', // 打枪计分方式：score=固定加分，multiplier=倍数
      sweepScoreValue: 2,     // 固定加分默认+2；倍数模式默认x2
      dealMode: 'single',     // 发牌方式：single/223/334/553/thirteen
      ghostMode: 0,           // 鬼牌模式：0=无鬼，1=单鬼，2=双鬼
      debugTestCards: false,  // 调试：下局给自己发三顺子测试手牌（鬼+3+5）
      roundCount: 20,         // 总局数
      limitType: 'round',     // round=按局数，time=按时长
      durationMinutes: 0,
      expireTime: 0,
      extraSuits: []
    },

    // 游戏状态：idle（空闲）、dealing（发牌中）、arranging（摆牌中）、comparing（比牌中）、settling（结算中）
    gameStatus: 'idle',

    // 玩家列表，包含人类玩家和AI玩家
    players: [],

    // 当前操作玩家的ID（通常为人类玩家）
    currentPlayerId: null,

    // 当前使用的牌组实例
    deck: null,

    // 当前回合数（从1开始）
    round: 0,

    // 倒计时剩余秒数
    countdown: 0,
    // 倒计时定时器引用，用于清除定时器
    countdownTimer: null,

    // 比牌结果列表
    compareResults: [],

    // 游戏历史记录，每局结束后追加
    history: [],

    // 发牌队列（用于发牌动画），按轮着发的顺序存储 [playerId, card]
    dealingQueue: []
  },

  getters: {
    // 获取当前玩家对象（根据 currentPlayerId 查找）
    currentPlayer: state => {
      return state.players.find(p => p.id === state.currentPlayerId)
    },

    // 获取人类玩家对象（isAI 为 false 的玩家）
    humanPlayer: state => {
      return state.players.find(p => !p.isAI)
    },

    // 获取所有 AI 玩家列表
    aiPlayers: state => {
      return state.players.filter(p => p.isAI)
    },

    // 获取已确认摆牌的玩家数量
    confirmedCount: state => {
      return state.players.filter(p => p.isLayoutConfirmed).length
    },

    // 判断所有玩家是否都已确认摆牌
    allConfirmed: state => {
      return state.players.every(p => p.isLayoutConfirmed)
    },

    // 获取未乌龙（未弃牌）的有效玩家列表
    validPlayers: state => {
      return state.players.filter(p => !p.isFolded)
    }
  },

  mutations: {
    // 设置游戏配置
    SET_CONFIG(state, config) {
      const next = { ...state.config, ...config }
      if (config.rowSpecialScores) {
        next.rowSpecialScores = {
          ...(state.config.rowSpecialScores || {}),
          ...config.rowSpecialScores
        }
      }
      state.config = next
    },
    
    // 设置游戏状态
    SET_GAME_STATUS(state, status) {
      state.gameStatus = status
    },
    
    // 设置玩家列表
    SET_PLAYERS(state, players) {
      state.players = players
    },
    
    // 添加玩家
    ADD_PLAYER(state, player) {
      state.players.push(player)
    },
    
    // 设置当前玩家
    SET_CURRENT_PLAYER(state, playerId) {
      state.currentPlayerId = playerId
    },

    // 设置玩家昵称
    SET_PLAYER_NAME(state, name) {
      state.playerName = name
    },

    // 设置玩家ID
    SET_PLAYER_ID(state, id) {
      state.playerId = id
    },
    
    // 设置牌组
    SET_DECK(state, deck) {
      state.deck = deck
    },
    
    // 设置玩家手牌
    SET_PLAYER_HANDS(state, { playerId, hands }) {
      const player = state.players.find(p => p.id === playerId)
      if (player) {
        player.hands = hands
      }
    },
    
    // 设置玩家摆牌
    SET_PLAYER_LAYOUT(state, { playerId, layout }) {
      const player = state.players.find(p => p.id === playerId)
      if (player) {
        player.layout = layout
      }
    },
    
    // 设置玩家乌龙状态
    SET_PLAYER_FOLDED(state, { playerId, isFolded }) {
      const player = state.players.find(p => p.id === playerId)
      if (player) {
        player.isFolded = isFolded
      }
    },
    
    // 设置玩家特殊牌型
    SET_PLAYER_SPECIAL_HAND(state, { playerId, specialHand }) {
      const player = state.players.find(p => p.id === playerId)
      if (player) {
        player.specialHand = specialHand
      }
    },
    
    // 设置玩家确认状态
    SET_PLAYER_CONFIRMED(state, { playerId, confirmed }) {
      const player = state.players.find(p => p.id === playerId)
      if (player) {
        player.isLayoutConfirmed = confirmed
      }
    },
    
    // 设置倒计时
    SET_COUNTDOWN(state, seconds) {
      state.countdown = seconds
    },
    
    // 减少倒计时
    DECREMENT_COUNTDOWN(state) {
      if (state.countdown > 0) {
        state.countdown--
      }
    },
    
    // 设置倒计时定时器
    SET_COUNTDOWN_TIMER(state, timer) {
      if (state.countdownTimer) {
        clearInterval(state.countdownTimer)
      }
      state.countdownTimer = timer
    },
    
    // 清除倒计时
    CLEAR_COUNTDOWN(state) {
      if (state.countdownTimer) {
        clearInterval(state.countdownTimer)
        state.countdownTimer = null
      }
      state.countdown = 0
    },
    
    // 设置比牌结果
    SET_COMPARE_RESULTS(state, results) {
      state.compareResults = results
    },
    
    // 更新玩家分数
    UPDATE_PLAYER_SCORE(state, { playerId, score, roomRateScore }) {
      const player = state.players.find(p => p.id === playerId)
      if (player) {
        player.score = score
        player.totalScore += score
        if (roomRateScore !== undefined) {
          player.roomRateScore = roomRateScore
          player.totalRoomRateScore = (player.totalRoomRateScore || 0) + roomRateScore
        }
      }
    },
    
    // 设置玩家总分（用于初始化或重置）
    SET_PLAYER_TOTAL_SCORE(state, { playerId, totalScore }) {
      const player = state.players.find(p => p.id === playerId)
      if (player) {
        player.totalScore = totalScore
      }
    },
    
    // 增加回合
    INCREMENT_ROUND(state) {
      state.round++
    },

    // 同步服务端回合数
    SET_ROUND(state, round) {
      state.round = Number(round) || 0
    },
    
    // 重置游戏（完整重置，包括局数和总分）
    RESET_GAME(state) {
      state.gameStatus = 'idle'
      state.round = 0
      state.compareResults = []
      state.players.forEach(p => {
        p.hands = []
        p.layout = { front: [], middle: [], back: [] }
        p.isFolded = false
        p.specialHand = null
        p.score = 0
        p.totalScore = 0  // 重置总分（用于重新开始游戏）
        p.roomRateScore = 0
        p.totalRoomRateScore = 0
        p.isLayoutConfirmed = false
      })
      if (state.countdownTimer) {
        clearInterval(state.countdownTimer)
        state.countdownTimer = null
      }
      state.countdown = 0
    },

    // 设置发牌队列
    SET_DEALING_QUEUE(state, queue) {
      state.dealingQueue = queue
    },

    // 发一张牌（从队列头部取出给对应玩家）
    DEAL_ONE_CARD(state) {
      const item = state.dealingQueue.shift()
      if (item && item.card) {
        const player = state.players.find(p => p.id === item.playerId)
        if (player) {
          player.hands.push(item.card)
        }
      }
    },

    // 重置玩家状态（不重置局数和总分）
    RESET_PLAYERS_STATE(state) {
      state.gameStatus = 'idle'
      state.compareResults = []
      state.players.forEach(p => {
        p.hands = []
        p.layout = { front: [], middle: [], back: [] }
        p.isFolded = false
        p.specialHand = null
        p.score = 0  // 每局开始时重置本局得分
        p.roomRateScore = 0
        // totalScore 不重置，继续累加
        p.isLayoutConfirmed = false
      })
      if (state.countdownTimer) {
        clearInterval(state.countdownTimer)
        state.countdownTimer = null
      }
      state.countdown = 0
    },
    
    // 添加历史记录
    ADD_HISTORY(state, record) {
      state.history.push(record)
    }
  },

  actions: {
    /**
     * 初始化单机游戏。
     *
     * 只负责创建玩家和回到 idle 状态，不会立即发牌。
     * 用户点击开始游戏后才会调用 startGame 进入第一局。
     */
    // 初始化游戏：创建人类玩家和指定数量的AI玩家，并设置为当前玩家
    initGame({ commit }, { playerName, aiCount = 3 }) {
      const players = []
      
      // 创建人类玩家
      const humanPlayer = createHumanPlayer(playerName)
      players.push(humanPlayer)
      commit('SET_CURRENT_PLAYER', humanPlayer.id)
      
      // 创建AI玩家
      for (let i = 0; i < aiCount; i++) {
        players.push(createAIPlayer(i))
      }
      
      commit('SET_PLAYERS', players)
      commit('SET_GAME_STATUS', 'idle')
    },
    
    // 开始新游戏（第一局）：完整重置游戏状态并进入发牌
    startGame({ commit, dispatch }) {
      commit('RESET_GAME')
      commit('INCREMENT_ROUND')
      dispatch('dealCards')
    },

    // 开始下一局（非第一局）：只重置玩家状态，不清空局数，然后发牌
    startNextRound({ commit, dispatch }) {
      // 只重置玩家状态，不重置局数
      commit('RESET_PLAYERS_STATE')
      commit('INCREMENT_ROUND')
      dispatch('dealCards')
    },
    
    /**
     * 发牌：根据玩家数量计算所需牌组，洗牌后构建轮着发的队列，进入发牌中状态。
     *
     * dealingQueue 是给 Game.vue 播放逐张发牌动画用的；真正的 hands 也会同步写入玩家对象。
     * 这样即使动画被中断，数据状态仍然完整。
     */
    dealCards({ commit, state }) {
      const playerCount = state.players.length
      const allCards = buildConfiguredDeck(playerCount, state.config.extraSuits, state.config.ghostMode || 0)

      // 洗牌
      for (let i = allCards.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [allCards[i], allCards[j]] = [allCards[j], allCards[i]]
      }

      // 构建轮着发的队列，根据发牌方式决定每轮发牌数量
      const handsByPlayerId = {}
      let cardIndex = 0
      state.players.forEach(player => {
        handsByPlayerId[player.id] = allCards.slice(cardIndex, cardIndex + 13)
        cardIndex += 13
      })
      const queue = buildDealingQueue(state.players, handsByPlayerId, state.config.dealMode || 'single')

      // 调试模式：给自己发特殊牌型测试计分规则
      if (state.config.debugTestCards) {
        const testCards = buildTestCards()
        const humanPlayerId = state.players[0].id
        let testIndex = 0
        for (let i = 0; i < queue.length; i++) {
          if (queue[i].playerId === humanPlayerId) {
            queue[i].card = testCards[testIndex++]
          }
        }
      }

      commit('SET_DEALING_QUEUE', queue)
      commit('SET_GAME_STATUS', 'dealing')
    },

    // 完成发牌：把剩余队列一次性发完，检测特殊牌型，进入摆牌
    finishDeal({ commit, state, dispatch }) {
      // 一次性发完剩余所有牌
      while (state.dealingQueue.length > 0) {
        commit('DEAL_ONE_CARD')
      }

      // 检测特殊牌型
      if (state.config.enableSpecialHands) {
        state.players.forEach(player => {
          if (player.hands.length === 13) {
            const specialHand = detectSpecialHand(player.hands)
            if (specialHand) {
              commit('SET_PLAYER_SPECIAL_HAND', {
                playerId: player.id,
                specialHand: applyConfiguredSpecialScore(specialHand, state.config)
              })
            }
          }
        })
      }

      commit('SET_GAME_STATUS', 'arranging')
      commit('SET_COUNTDOWN', state.config.arrangeTime)

      // 启动倒计时
      const timer = setInterval(() => {
        if (state.countdown > 0) {
          commit('DECREMENT_COUNTDOWN')
        } else {
          commit('CLEAR_COUNTDOWN')
          dispatch('autoConfirmAll')
        }
      }, 1000)
      commit('SET_COUNTDOWN_TIMER', timer)
    },
    
    // 玩家摆牌：将玩家选择的牌型布局保存到状态中
    playerArrange({ commit }, { playerId, layout }) {
      commit('SET_PLAYER_LAYOUT', { playerId, layout })
    },
    
    /**
     * 玩家确认摆牌。
     *
     * reportSpecial=true：点击「报道」——三道按特殊牌型计分。
     * reportSpecial=false/缺省：默认普通比牌，不按特殊牌型计分。
     */
    confirmLayout({ commit, getters, dispatch, state }, { playerId, layout, reportSpecial = false }) {
      const player = state.players.find(p => p.id === playerId)
      const safeLayout = layout || { front: [], middle: [], back: [] }
      commit('SET_PLAYER_LAYOUT', { playerId, layout: safeLayout })

      if (reportSpecial && state.config.enableSpecialHands && player) {
        // 报道：保留已有特殊牌，或根据手牌/layout 再检测（三同花、三顺子等）
        const specialHand = detectSpecialHand(player.hands, safeLayout)
        if (specialHand) {
          commit('SET_PLAYER_SPECIAL_HAND', {
            playerId,
            specialHand: applyConfiguredSpecialScore(specialHand, state.config)
          })
        } else {
          commit('SET_PLAYER_SPECIAL_HAND', { playerId, specialHand: null })
        }
      } else {
        // 默认：放弃特殊牌型，走普通比牌
        commit('SET_PLAYER_SPECIAL_HAND', { playerId, specialHand: null })
      }

      const hasSpecialHand = state.config.enableSpecialHands && player && player.specialHand
      if (!hasSpecialHand && !state.config.allowFouled) {
        const layoutCheck = validateNormalLayout(safeLayout)
        if (!layoutCheck.valid) {
          dispatch('foldPlayer', playerId)
          return
        }
      }

      commit('SET_PLAYER_CONFIRMED', { playerId, confirmed: true })

      if (getters.allConfirmed) {
        dispatch('startComparing')
      }
    },

    // 自动确认所有玩家：倒计时结束时调用
    // 超时未报道 → 一律按普通比牌自动摆牌（有特殊牌型也不自动报道）
    autoConfirmAll({ state, dispatch }) {
      state.players.forEach(player => {
        if (player.isLayoutConfirmed) return

        const hasLayout = hasAnyLayoutCards(player.layout)

        if (hasLayout) {
          dispatch('confirmLayout', { playerId: player.id, layout: player.layout, reportSpecial: false })
        } else if (player.hands.length > 0) {
          const { aiArrange } = require('@/utils/ai')
          const layout = aiArrange(player.hands)
          dispatch('confirmLayout', { playerId: player.id, layout, reportSpecial: false })
        } else {
          dispatch('foldPlayer', player.id)
        }
      })
    },
    
    // 玩家乌龙：将玩家标记为乌龙（弃牌）状态，并视为已确认
    foldPlayer({ commit, getters, dispatch }, playerId) {
      commit('SET_PLAYER_FOLDED', { playerId, isFolded: true })
      commit('SET_PLAYER_CONFIRMED', { playerId, confirmed: true })
      if (getters.allConfirmed) {
        dispatch('startComparing')
      }
    },
    
    /**
     * 开始比牌：清除倒计时，进入比牌状态，执行比牌逻辑并保存结果。
     *
     * 注意这里不会立刻 settleGame。前端需要先播放倒计时、逐道翻牌、打枪动画；
     * 动画全部完成后由 Game.vue 主动 dispatch settleGame。
     */
    async startComparing({ commit, dispatch }) {
      commit('CLEAR_COUNTDOWN')
      commit('SET_GAME_STATUS', 'comparing')
      
      // 执行比牌
      const results = await dispatch('compareAllPlayers')
      commit('SET_COMPARE_RESULTS', results)
      
      // 不再自动进入结算，由Game.vue控制比牌动画完成后进入结算
    },
    
    /**
     * 比牌逻辑。
     *
     * 特殊牌型玩家先按特殊牌型规则和所有有效玩家结算；
     * 非特殊玩家再两两逐道比牌。非法普通摆牌会在这里兜底标记乌龙。
     */
    async compareAllPlayers({ state, getters, commit, dispatch }) {
      const results = []

      // 检查特殊牌型。特殊牌型玩家按特殊牌型/奇牌规则结算，不再参与普通两两比牌。
      const activeSpecialPlayers = getActiveSpecialPlayers(getters.validPlayers)
      const activeSpecialIds = new Set(activeSpecialPlayers.map(p => p.id))

      // 非特殊玩家必须完成合法摆牌，否则按乌龙处理。
      // allowFouled 只表示允许玩家提交倒水牌，不表示结算时跳过乌龙判定。
      getters.validPlayers.forEach(player => {
        if (activeSpecialIds.has(player.id)) return
        const layoutCheck = validateNormalLayout(player.layout)
        if (!layoutCheck.valid) {
          commit('SET_PLAYER_FOLDED', { playerId: player.id, isFolded: true })
        }
      })

      if (activeSpecialPlayers.length > 0) {
        const specialResults = await dispatch('settleSpecialHands', activeSpecialPlayers)
        results.push(...specialResults)
        if (state.config.allowOddCards) {
          results.push(...settleOddCardRows(activeSpecialPlayers, state.players, state.config))
        }
      }

      const normalPlayers = state.players.filter(p => !p.isFolded && !activeSpecialIds.has(p.id))
      const foldedPlayers = state.players.filter(p => p.isFolded && !activeSpecialIds.has(p.id))
      for (let i = 0; i < normalPlayers.length; i++) {
        for (let j = i + 1; j < normalPlayers.length; j++) {
          const player1 = normalPlayers[i]
          const player2 = normalPlayers[j]
          
          const result = compareTwoPlayers(player1, player2, state.config)
          if (result) results.push(result)
        }
      }

      // 乌龙玩家与未乌龙玩家比牌时，乌龙全输、未乌龙全赢。
      // 直接生成“三道全输 + 打枪”的普通比牌结果，后续结算自然复用基础、打枪、马牌、全垒打、油菜规则。
      normalPlayers.forEach(normalPlayer => {
        foldedPlayers.forEach(foldedPlayer => {
          results.push(createFoldedCompareResult(normalPlayer, foldedPlayer, state.config))
        })
      })
      
      return results
    },
    
    // 结算特殊牌型：处理至尊青龙、一条龙等顶级牌型，以及普通特殊牌型的差额结算
    settleSpecialHands({ state }, specialPlayers) {
      const results = []
      const players = state.players

      if (specialPlayers.length === 0) return results

      const specialIds = new Set(specialPlayers.map(p => p.id))
      const sortedSpecials = [...specialPlayers].sort((a, b) => b.specialHand.score - a.specialHand.score)

      players.forEach(player => {
        if (!specialIds.has(player.id)) {
          // 非当前有效特殊牌型玩家：输给所有有效特殊牌型玩家（全额）
          sortedSpecials.forEach(specialPlayer => {
            results.push({
              type: 'special',
              winner: specialPlayer.id,
              loser: player.id,
              winnerName: specialPlayer.name,
              loserName: player.name,
              specialHand: specialPlayer.specialHand,
              score: specialPlayer.specialHand.score * state.config.baseScore
            })
          })
        } else {
          // 有效特殊牌型玩家之间：大牌型吃小牌型，差额结算；同分不输赢
          sortedSpecials.forEach(other => {
            if (other.id === player.id) return
            if (other.specialHand.score > player.specialHand.score) {
              results.push({
                type: 'special',
                winner: other.id,
                loser: player.id,
                winnerName: other.name,
                loserName: player.name,
                specialHand: other.specialHand,
                score: (other.specialHand.score - player.specialHand.score) * state.config.baseScore
              })
            }
          })
        }
      })

      return results
    },
    
    /**
     * 游戏结算。
     *
     * compareResults 只记录每道赢家和倍率；本方法统一乘底分并落到 player.score/totalScore。
     *
     * 单机模式的最终计分入口就在这里。流程要和后端 GameService.settleGame 保持一致：
     * 1. 特殊牌型先结算，奇牌只补每道额外分；
     * 2. 普通玩家两两比较三道基础分，并记录这一对玩家三道基础分的净胜负；
     * 3. 打枪、乌龙、全垒打按规则补分，马牌倍数在 addPairScore 中统一处理；
     * 4. 油菜分放在基础/打枪/马牌/全垒打之后，但判定依据只看这一对玩家“三道基础分合计”谁赢；
     * 5. 开房积分不进入玩家总分，只单独累计 roomRateScore/totalRoomRateScore。
     */
    settleGame({ commit, state }) {
      commit('SET_GAME_STATUS', 'settling')

      // 计算每个玩家的得分
      const playerScores = {}
      const roomRateScores = {}
      state.players.forEach(p => {
        playerScores[p.id] = 0
        roomRateScores[p.id] = 0
      })
      const pendingYucaiSettlements = []

      // 根据比牌结果逐行计算分数（和UI的playerCompareSummary逻辑完全一致）
      state.compareResults.forEach(result => {
        if (result.type === 'special' || result.type === 'odd') {
          if (result.winner && result.loser) {
            addPairScore(playerScores, roomRateScores, result.winner, result.loser, result.score, state.config, state.players, {
              roomRateEligible: result.type === 'special'
            })
          }
          return
        }

        // 逐行计分：头道/中道/尾道。pairBaseNet 只统计三道基础分净胜负，
        // 油菜规则会在三道都算完后根据这个净胜负额外加/扣一次。
        const rows = ['front', 'middle', 'back']
        let pairBaseNet = 0
        rows.forEach(row => {
          if (result[row] && result[row].winner) {
            // 输家是这对中的另一方
            const loserId = result[row].winner === result.player1 ? result.player2 : result.player1
            const rowScore = result[row].score * state.config.baseScore
            addPairScore(playerScores, roomRateScores, result[row].winner, loserId, rowScore, state.config, state.players)
            pairBaseNet += result[row].winner === result.player1 ? rowScore : -rowScore
          }
        })

        // 油菜按“两名玩家三道基础分合计”只结算一次，不按每道重复结算。
        // 这里先排队，等打枪、乌龙、全垒打等前置规则处理完后再统一落分。
        pendingYucaiSettlements.push(() => {
          applyYucaiScore(playerScores, roomRateScores, result, pairBaseNet, state.config, state.players)
        })

        // 打枪奖励：按房间设置计算固定加分或倍数奖励。
        if (result.sweep && result.winner && result.loser) {
          const sweepBonus = getSweepBonus(result, state.config) * state.config.baseScore
          addPairScore(playerScores, roomRateScores, result.winner, result.loser, sweepBonus, state.config, state.players)
        }
      })

      const foldedPlayers = state.players.filter(p => p.isFolded)
      const unfoldedPlayers = state.players.filter(p => !p.isFolded)

      // 全垒打/通关处理：未乌龙玩家打枪所有其他玩家时，对每家再翻一倍；两人局不计算全垒打。
      const validPlayerIds = unfoldedPlayers.map(p => p.id)
      const sweepResults = state.compareResults.filter(r => r.sweep && r.type !== 'special')
      const hasSpecialHandResult = state.compareResults.some(r => r.type === 'special')

      if (!hasSpecialHandResult && state.config.enableHomeRun !== false && state.players.length > 2) {
        validPlayerIds.forEach(playerId => {
          const sweptOpponents = new Set()
          sweepResults.forEach(r => {
            if (r.winner === playerId) {
              sweptOpponents.add(r.loser)
            }
          })
          const otherValidPlayers = state.players.map(p => p.id).filter(id => id !== playerId)
          const isHomeRun = otherValidPlayers.length > 0 && otherValidPlayers.every(id => sweptOpponents.has(id))
          if (isHomeRun) {
            otherValidPlayers.forEach(opponentId => {
              const sweepResult = sweepResults.find(r => r.winner === playerId && r.loser === opponentId)
              const homeRunBonus = (sweepResult ? sweepResult.score : 5) * state.config.baseScore
              addPairScore(playerScores, roomRateScores, playerId, opponentId, homeRunBonus, state.config, state.players, {
                roomRateEligible: true
              })
            })
          }
        })
      }

      pendingYucaiSettlements.forEach(applySettlement => applySettlement())

      // 更新玩家分数
      Object.entries(playerScores).forEach(([playerId, score]) => {
        commit('UPDATE_PLAYER_SCORE', { playerId, score, roomRateScore: roomRateScores[playerId] || 0 })
      })

      // 添加历史记录
      commit('ADD_HISTORY', {
        round: state.round,
        results: state.compareResults,
        playerScores: { ...playerScores },
        roomRateScores: { ...roomRateScores }
      })
    },
    
    // 下一局：触发开始下一局流程
    nextRound({ dispatch }) {
      dispatch('startNextRound')
    }
  },

  modules: {
    multiplayer
  }
})

// 检查玩家是否有完整的摆牌（头道3张、中道5张、尾道5张）
function hasCompleteLayout(player) {
  return player.layout.front.length === 3 && 
         player.layout.middle.length === 5 && 
         player.layout.back.length === 5
}

function hasAnyLayoutCards(layout) {
  if (!layout) return false
  return (layout.front && layout.front.length > 0) ||
    (layout.middle && layout.middle.length > 0) ||
    (layout.back && layout.back.length > 0)
}

function validateNormalLayout(layout) {
  if (!layout ||
      !Array.isArray(layout.front) ||
      !Array.isArray(layout.middle) ||
      !Array.isArray(layout.back) ||
      layout.front.length !== 3 ||
      layout.middle.length !== 5 ||
      layout.back.length !== 5) {
    return { valid: false, reason: '牌数不正确' }
  }

  return isValidLayout(layout.front, layout.middle, layout.back)
}

function getActiveSpecialPlayers(players) {
  const specialPlayers = players.filter(p => p.specialHand)
  return specialPlayers
}

/**
 * 计算某道获胜倍率。
 *
 * 头道三条按 3 分；中道和尾道按 requirements.md 中的规则，对葫芦、
 * 铁支、同花顺、五同给额外倍率。
 */
function getRowWinScore(row, winner, config = {}) {
  if (row === 'front') {
    const frontCards = winner.layout.front
    if (!frontCards || frontCards.length !== 3) return 1
    return evaluateThreeCards(frontCards).rank.value === 4
      ? pickRowSpecialScore(config, 'frontThreeKind', 3)
      : 1
  }

  const cards = winner.layout[row]
  if (!cards || cards.length !== 5) return 1

  const handRank = evaluateFiveCards(cards).rank.value
  if (row === 'middle') {
    if (handRank === 10) return pickRowSpecialScore(config, 'middleFiveKind', 20)
    if (handRank === 9) return pickRowSpecialScore(config, 'middleStraightFlush', 10)
    if (handRank === 8) return pickRowSpecialScore(config, 'middleFourKind', 8)
    if (handRank === 7) return pickRowSpecialScore(config, 'middleFullHouse', 2)
  }

  if (row === 'back') {
    if (handRank === 10) return pickRowSpecialScore(config, 'backFiveKind', 10)
    if (handRank === 9) return pickRowSpecialScore(config, 'backStraightFlush', 5)
    if (handRank === 8) return pickRowSpecialScore(config, 'backFourKind', 4)
  }

  return 1
}

function getOddRowScore(row, player, config = {}) {
  if (!player || !hasCompleteLayout(player)) return 0
  const rowScore = getRowWinScore(row, player, config)
  return rowScore > 1 ? rowScore : 0
}

function settleOddCardRows(specialPlayers, players, config = {}) {
  const results = []
  specialPlayers.forEach(specialPlayer => {
    const oddScore = ['front', 'middle', 'back'].reduce((sum, row) => {
      return sum + getOddRowScore(row, specialPlayer, config)
    }, 0)
    if (oddScore <= 0) return

    players.forEach(player => {
      if (player.id === specialPlayer.id) return
      results.push({
        type: 'odd',
        winner: specialPlayer.id,
        loser: player.id,
        winnerName: specialPlayer.name,
        loserName: player.name,
        specialHand: {
          type: 'ODD_CARD',
          name: '奇牌',
          score: oddScore
        },
        score: oddScore * (config.baseScore || 1)
      })
    })
  })
  return results
}

function getSweepBonus(result, config = {}) {
  const rowScoreTotal = ['front', 'middle', 'back'].reduce((sum, row) => {
    return sum + (result[row] && result[row].winner ? result[row].score : 0)
  }, 0)
  const value = Number(config.sweepScoreValue) || 2

  if (config.sweepScoreType === 'multiplier') {
    return rowScoreTotal * Math.max(value - 1, 0)
  }

  return value
}

function applyConfiguredSpecialScore(specialHand, config = {}) {
  if (!specialHand) return null

  const scoreMap = {
    DRAGON: config.dragonScore,
    ALL_BIG: config.allBigScore,
    ALL_SMALL: config.allSmallScore,
    SIX_PAIRS_HALF: config.sixPairsHalfScore,
    THREE_FLUSH: config.threeFlushScore,
    THREE_STRAIGHT: config.threeStraightScore
  }
  const configuredScore = Number(scoreMap[specialHand.type])
  return {
    ...specialHand,
    score: Number.isFinite(configuredScore) && configuredScore > 0
      ? Math.floor(configuredScore)
      : specialHand.score
  }
}

/**
 * 油菜计分。
 *
 * requirements.md 2.4 的新规则要求：
 * - 先完成一对玩家之间的三道基础计分；
 * - 只看三道基础分合计谁净胜，净胜方加油菜分，净负方扣油菜分；
 * - 基础分打平时不产生油菜；
 * - 油菜本身不再套马牌倍数，因为马牌额外翻倍已经在基础/打枪/全垒打等分值进入 addPairScore 时处理。
 */
function applyYucaiScore(playerScores, roomRateScores, result, pairBaseNet, config = {}, players = []) {
  const yucaiScore = config.enableYucai ? (Number(config.yucaiScore) || 0) : 0
  const yucaiBonus = Math.max(yucaiScore, 0) * config.baseScore
  if (yucaiBonus <= 0 || pairBaseNet === 0) return

  const winnerId = pairBaseNet > 0 ? result.player1 : result.player2
  const loserId = pairBaseNet > 0 ? result.player2 : result.player1
  addPairScore(playerScores, roomRateScores, winnerId, loserId, yucaiBonus, config, players, {
    applyHorse: false
  })
}

/**
 * 两人一项分值的通用落分函数。
 *
 * 只要规则表现为“赢家 +N，输家 -N”，都走这里，保证马牌倍数和开房积分
 * 不会散落在各个规则分支里。roomRateEligible=true 时只给赢家增加开房积分，
 * 不把开房积分混进玩家本局分和总分。
 */
function addPairScore(playerScores, roomRateScores, winnerId, loserId, score, config = {}, players = [], options = {}) {
  if (!winnerId || !loserId || !score) return
  const finalScore = applyPairScoreOptions(score, winnerId, loserId, config, players, options)
  playerScores[winnerId] = (playerScores[winnerId] || 0) + finalScore
  playerScores[loserId] = (playerScores[loserId] || 0) - finalScore
  if (options.roomRateEligible && roomRateScores) {
    roomRateScores[winnerId] = (roomRateScores[winnerId] || 0) + getRoomRateBonus(finalScore, config)
  }
}

function applyPairScoreOptions(score, winnerId, loserId, config = {}, players = [], options = {}) {
  const horseMultiplier = options.applyHorse === false ? 1 : getHorseMultiplier(winnerId, loserId, config, players)
  return score * horseMultiplier
}

function getHorseMultiplier(playerId1, playerId2, config = {}, players = []) {
  if (!config.enableHorse) return 1
  const player1 = players.find(p => p.id === playerId1)
  const player2 = players.find(p => p.id === playerId2)
  return hasHorseCard(player1, config) || hasHorseCard(player2, config) ? 2 : 1
}

function hasHorseCard(player, config = {}) {
  if (!player || !Array.isArray(player.hands)) return false
  const horseRank = Number(config.horseRank) || 1
  const horseSuit = config.horseSuit || 'hearts'
  return player.hands.some(card => {
    return card &&
      card.rank &&
      card.suit &&
      Number(card.rank.value) === horseRank &&
      card.suit.name === horseSuit
  })
}

function getRoomRateBonus(score, config = {}) {
  if (!config.allowRoomRate || score <= 0) return 0
  const percent = Number(config.roomRatePercent) || 2
  return Math.ceil(score * Math.max(percent, 0) / 100)
}

/**
 * 比较两个普通玩家。
 *
 * 只生成两人之间的对比结果，不直接写分。
 * 这样同一份 compareResults 可以同时用于动画展示、玩家摘要和最终结算。
 */
function compareTwoPlayers(player1, player2, config = {}) {
  const result = {
    player1: player1.id,
    player2: player2.id,
    player1Name: player1.name,
    player2Name: player2.name,
    front: null,
    middle: null,
    back: null,
    sweep: false,
    score: 0
  }
  
  // 检查双方是否都有完整的摆牌
  const player1Complete = hasCompleteLayout(player1)
  const player2Complete = hasCompleteLayout(player2)
  
  // 如果一方或双方没有完整摆牌（乌龙），不参与逐墩比牌
  // 乌龙惩罚由 settleGame 统一处理
  if (!player1Complete || !player2Complete) {
    return null
  }
  
  let player1Wins = 0
  let player2Wins = 0
  
  // 比较头道
  const frontCompare = compareThreeCards(player1.layout.front, player2.layout.front)
  if (frontCompare > 0) {
    result.front = { winner: player1.id, score: getRowWinScore('front', player1, config) }
    player1Wins++
  } else if (frontCompare < 0) {
    result.front = { winner: player2.id, score: getRowWinScore('front', player2, config) }
    player2Wins++
  } else {
    result.front = { winner: null, score: 0 }
  }
  
  // 比较中道
  const middleCompare = compareFiveCards(player1.layout.middle, player2.layout.middle)
  if (middleCompare > 0) {
    result.middle = { winner: player1.id, score: getRowWinScore('middle', player1, config) }
    player1Wins++
  } else if (middleCompare < 0) {
    result.middle = { winner: player2.id, score: getRowWinScore('middle', player2, config) }
    player2Wins++
  } else {
    result.middle = { winner: null, score: 0 }
  }
  
  // 比较尾道
  const backCompare = compareFiveCards(player1.layout.back, player2.layout.back)
  if (backCompare > 0) {
    result.back = { winner: player1.id, score: getRowWinScore('back', player1, config) }
    player1Wins++
  } else if (backCompare < 0) {
    result.back = { winner: player2.id, score: getRowWinScore('back', player2, config) }
    player2Wins++
  } else {
    result.back = { winner: null, score: 0 }
  }
  
  // 判断是否打枪
  const rowScoreTotal = ['front', 'middle', 'back'].reduce((sum, row) => {
    return sum + (result[row] && result[row].winner ? result[row].score : 0)
  }, 0)
  if (player1Wins === 3) {
    result.sweep = true
    result.winner = player1.id
    result.loser = player2.id
    result.score = rowScoreTotal + getSweepBonus(result, config)
  } else if (player2Wins === 3) {
    result.sweep = true
    result.winner = player2.id
    result.loser = player1.id
    result.score = rowScoreTotal + getSweepBonus(result, config)
  } else {
    // 计算净胜分
    const netScore = player1Wins - player2Wins
    if (netScore > 0) {
      result.winner = player1.id
      result.loser = player2.id
      result.score = netScore
    } else if (netScore < 0) {
      result.winner = player2.id
      result.loser = player1.id
      result.score = -netScore
    } else {
      result.winner = null
      result.loser = null
      result.score = 0
    }
  }

  return result
}

function createFoldedCompareResult(winner, foldedPlayer, config = {}) {
  const result = {
    type: 'fold',
    player1: winner.id,
    player2: foldedPlayer.id,
    player1Name: winner.name,
    player2Name: foldedPlayer.name,
    front: { winner: winner.id, score: getRowWinScore('front', winner, config) },
    middle: { winner: winner.id, score: getRowWinScore('middle', winner, config) },
    back: { winner: winner.id, score: getRowWinScore('back', winner, config) },
    sweep: true,
    winner: winner.id,
    loser: foldedPlayer.id,
    score: 0
  }
  const rowScoreTotal = ['front', 'middle', 'back'].reduce((sum, row) => sum + result[row].score, 0)
  result.score = rowScoreTotal + getSweepBonus(result, config)
  return result
}
